import { useEffect, useMemo, useState } from "react";
import api, { brl } from "../lib/api";
import { openPdf } from "../lib/fiscal";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Receipt, FileText, Calendar,
  TrendingDown, TrendingUp, Wallet, Sparkles,
} from "lucide-react";

const now = new Date();

const EMPTY = {
  category: "Outros",
  description: "",
  amount: 0,
  payment_method: "dinheiro",
  date: "",
};

const PAY = [
  { k: "dinheiro", label: "Dinheiro" },
  { k: "pix", label: "PIX" },
  { k: "cartao_credito", label: "Crédito" },
  { k: "cartao_debito", label: "Débito" },
  { k: "boleto", label: "Boleto" },
];

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function Expenses() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadCats = () =>
    api.get("/expenses/categories").then((r) => setCategories(r.data.categories || []));

  const load = () => {
    api
      .get(`/expenses/summary?year=${year}&month=${month}`)
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { load(); }, [year, month]);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Informe um valor maior que zero");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "despesa",
        category: form.category || "Outros",
        description: form.description,
        amount: Number(form.amount),
        payment_method: form.payment_method,
      };
      if (form.date) payload.date = new Date(form.date).toISOString();
      await api.post("/financial/transactions", payload);
      toast.success(`Gasto de ${brl(payload.amount)} registrado`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar gasto");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esse gasto?")) return;
    await api.delete(`/financial/transactions/${id}`);
    toast.success("Gasto removido");
    load();
  };

  const downloadReport = async () => {
    await openPdf(
      `/expenses/monthly-report?year=${year}&month=${month}`,
      `gastos-${year}-${String(month).padStart(2, "0")}.pdf`
    );
  };

  const total = summary?.total_expense || 0;
  const revenue = summary?.total_revenue || 0;
  const net = summary?.net || 0;
  const catList = summary?.by_category || [];
  const expenses = summary?.expenses || [];

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) arr.push(y);
    return arr;
  }, []);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
            Controle de Despesas
          </div>
          <h1 className="font-display text-4xl font-bold">Gastos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Registre tudo que você gasta durante o mês e gere um relatório completo no fim do período.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Mês</label>
            <select
              data-testid="exp-month"
              className="border border-slate-300 rounded-sm px-2 py-2 text-sm"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Ano</label>
            <select
              data-testid="exp-year"
              className="border border-slate-300 rounded-sm px-2 py-2 text-sm"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button
            data-testid="exp-report-btn"
            onClick={downloadReport}
            className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"
          >
            <FileText size={16} /> Relatório do mês (PDF)
          </button>
          <button
            data-testid="exp-new-btn"
            onClick={() => { setForm({ ...EMPTY }); setOpen(true); }}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"
          >
            <Plus size={16} /> Novo Gasto
          </button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
            <TrendingDown size={14} className="text-red-500"/> Gastos
          </div>
          <div className="font-display text-3xl font-bold text-red-600" data-testid="exp-kpi-total">{brl(total)}</div>
          <div className="text-xs text-slate-500 mt-1">{summary?.count || 0} lançamento(s)</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
            <TrendingUp size={14} className="text-emerald-500"/> Receita do mês
          </div>
          <div className="font-display text-3xl font-bold text-emerald-600">{brl(revenue)}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
            <Wallet size={14} className="text-[#2563EB]"/> Saldo líquido
          </div>
          <div className={`font-display text-3xl font-bold ${net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{brl(net)}</div>
        </div>
        <div className="bg-gradient-to-br from-[#0A1628] to-[#2563EB] border border-[#2563EB] p-5 rounded-sm text-white">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-200 mb-2 flex items-center gap-1">
            <Sparkles size={14}/> Dica IA
          </div>
          <div className="text-sm leading-tight">
            Fale com o assistente (canto inferior direito):
            <br/>
            <b>&quot;gastei 50 na gasolina hoje&quot;</b>
            <br/>
            e ele registra pra você.
          </div>
        </div>
      </div>

      {/* By category */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Distribuição</div>
            <h2 className="font-display text-lg font-bold">Gastos por categoria</h2>
          </div>
        </div>
        <div className="p-4">
          {catList.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">
              Nenhum gasto registrado nesse mês.
            </div>
          ) : (
            <div className="space-y-2">
              {catList.map((c) => (
                <div key={c.category} data-testid={`exp-cat-${c.category}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold">{c.category}</span>
                    <span className="text-slate-600">{brl(c.amount)} · {c.percent.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6]"
                      style={{ width: `${Math.min(100, c.percent)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Detalhes</div>
          <h2 className="font-display text-lg font-bold">Lançamentos do mês</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Categoria</th>
                <th className="text-left px-4 py-2">Descrição</th>
                <th className="text-left px-4 py-2">Pagamento</th>
                <th className="text-right px-4 py-2">Valor</th>
                <th className="w-12"/>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    Sem lançamentos. Clique em <b>Novo Gasto</b> ou fale com o assistente IA.
                  </td>
                </tr>
              ) : expenses.map((e) => {
                const d = (e.date || "").slice(0, 10);
                const [y, m, dd] = d.split("-");
                const display = y ? `${dd}/${m}/${y}` : "—";
                const pay = PAY.find((p) => p.k === e.payment_method)?.label || e.payment_method;
                return (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`exp-row-${e.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap flex items-center gap-1">
                      <Calendar size={12} className="text-slate-400"/> {display}
                    </td>
                    <td className="px-4 py-2 font-semibold">{e.category}</td>
                    <td className="px-4 py-2 text-slate-600">{e.description}</td>
                    <td className="px-4 py-2 text-slate-500">{pay}</td>
                    <td className="px-4 py-2 text-right font-semibold text-red-600">{brl(e.amount)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => remove(e.id)}
                        data-testid={`exp-del-${e.id}`}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Novo Gasto */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-lg w-full p-6" data-testid="exp-modal">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-[#2563EB]"/>
                <h2 className="font-display text-2xl font-bold">Novo Gasto</h2>
              </div>
              <button onClick={() => setOpen(false)}><X size={20}/></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">
                  Valor *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  autoFocus
                  data-testid="exp-amount"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-lg font-semibold"
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Categoria</label>
                <select
                  data-testid="exp-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Descrição</label>
                <input
                  data-testid="exp-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2"
                  placeholder="Ex: Gasolina moto, aluguel de julho..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Pagamento</label>
                  <select
                    data-testid="exp-payment"
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2"
                  >
                    {PAY.map((p) => <option key={p.k} value={p.k}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Data</label>
                  <input
                    type="date"
                    data-testid="exp-date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2"
                  />
                  <div className="text-[10px] text-slate-400 mt-1">Vazio = hoje</div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Cancelar
              </button>
              <button
                data-testid="exp-save-btn"
                disabled={saving}
                onClick={save}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold rounded-sm disabled:opacity-50"
              >
                {saving ? "Salvando..." : "Registrar gasto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
